import {
  keepPreviousData,
  useQuery,
  UseQueryOptions,
} from '@tanstack/react-query'
import apiClient from 'enrollment-platform/api/enrollment-platform-client/api-client'
import { AbsorptionType } from './reconciliation-v2-enums'
import { centsToDollars } from './reconciliation-v2-api-client'

/**
 * Client for `GET /reconciliation-v2/transactions` (+ `/summary`) — the
 * payment-reconciliation transaction ledger backing the Transaction Details
 * page. Mirrors the shape documented by the backend team; kept in its own
 * module (rather than folded into reconciliation-v2-api-client.ts) since it is
 * a standalone read surface for a single new page.
 */

export interface TransactionViewInsuredPerson {
  firstName: string
  lastName: string
  relationship: string
}

export interface AllocationTicket {
  ticketId: string | null
  ticketUrl: string
  /** Coverage month this ticket is about, YYYY-MM-DD. */
  periodStartDate: string
  note: string | null
  createdAt: string
}

export interface TransactionViewAllocation {
  benefitId: string
  benefitLabel: string
  /**
   * False when the benefit is outside recon-v2's benefits projection (deactivated
   * upstream, or missing from the sync), so `benefitLabel` is the bare id and no
   * name/carrier could be resolved. Rides `mapAllocation`'s spread unchanged.
   * Optional on the wire only in the sense that an older server omits it — treat
   * anything but an explicit `false` as resolved.
   */
  benefitResolved: boolean
  employeeName: string | null
  employeePublicKey: string | null
  carrierName: string | null
  companyName: string | null
  insuredPersons: TransactionViewInsuredPerson[]
  /** Coverage month, YYYY-MM-DD. */
  periodStartDate: string
  /** Signed decimal string. */
  amount: string
  classification: string | null
  /** Latest note from the Adjust Payment "Allocate" step for this (transaction, benefit, coverage-month). Null when none. */
  note: string | null
  /** Still-linked Zendesk tickets for this (benefit, coverage-month), latest-first, deduped by URL. */
  tickets: AllocationTicket[]
}

export interface TransactionViewAbsorption {
  id: string
  /** Positive dollar amount absorbed (written off). */
  amount: number
  absorptionType: AbsorptionType
  /**
   * The operator's justification for the write-off, restated on every absorb
   * submit (an absorption is never UPDATEd — see the enrollment-platform ADR
   * 0003). Null for absorptions recorded before the note existed.
   */
  note: string | null
  createdAt: string
}

/**
 * The benefit an unallocated transaction resolves to, when knowable — lets the
 * ledger show Carrier/Benefit ID/Company/Insured on a not-yet-allocated row.
 * No period/month/amount (those exist only once allocated).
 */
export interface ResolvedBenefit {
  benefitId: string
  benefitLabel: string
  carrierName: string | null
  companyName: string | null
  employeeName: string | null
  employeePublicKey: string | null
  insuredPersons: TransactionViewInsuredPerson[]
}

/** An operator return on a transaction (finance.transaction_return). */
export interface TransactionViewReturn {
  note: string
  returnedBy: string
  returnedAt: string
}

export interface TransactionViewRow {
  transactionId: string
  /** Human-facing all_payment_transaction.transaction_number. */
  transactionNumber: string | null
  transactionDate: string
  /** Signed. */
  amount: number
  transactionType: string
  transactionStatus: string
  /** 'marqeta' | 'wex' | 'i_stream' | 'check' | 'group_ach' | 'ee_paid' | ... */
  source: string
  token: string | null
  checkNumber: string | null
  allocatedAmount: number
  allocations: TransactionViewAllocation[]
  /** Absorptions (written-off excess) on this transaction; shown as sub-rows. */
  absorptions: TransactionViewAbsorption[]
  /** Sum of absorption amounts (positive dollars). */
  absorbedAmount: number
  /** Set only for a row with no allocation whose benefit is resolvable; else null. */
  resolvedBenefit: ResolvedBenefit | null
  /**
   * The active return, or null. A returned tx is out of the review queue and the
   * Pending count; it still lists here with a "Returned" recon status. Optional
   * so an EPS that predates returns still parses.
   */
  txReturn?: TransactionViewReturn | null
}

export interface ListTransactionsResponse {
  data: TransactionViewRow[]
  total: number
  page: number
  limit: number
  year: number
}

export interface TransactionSummaryResponse {
  reconciledCount: number
  pendingCount: number
  totalAmount: number
  /** Sum of absorptions over the filtered scope (positive dollars). */
  absorbedAmount: number
  /** Count of returned transactions — excluded from pendingCount and totalAmount. */
  returnedCount: number
  /** Signed dollar sum of returned transactions. */
  returnedAmount: number
}

export interface ListTransactionsParams {
  year?: number
  month?: number
  source?: string
  /** Comma-separated list of source enum values, e.g. "check,marqeta". */
  sources?: string
  status?: string
  transactionType?: 'charge' | 'refund' | 'chargeback'
  allocated?: boolean
  benefitId?: string
  companyName?: string
  /** Client public key — same filter as the Benefits tab's `clientId`. */
  clientId?: string
  splitOnly?: boolean
  /** When true, restrict to transactions that carry at least one absorption. */
  hasAbsorption?: boolean
  /** 1–12. */
  coverageMonth?: number
  /** Comma-separated subset of `reconciled,pending,refund,absorbed,returned`; omit for all. */
  reconStatus?: string
  search?: string
  /**
   * Comma-separated subset of `absorption,return,allocation`: restrict to
   * transactions carrying a non-blank note of a listed type. Omit for no filter.
   */
  noteTypes?: string
  /** Free text matched against the notes (of `noteTypes`, when set). Separate from `search`. */
  noteSearch?: string
  limit: number
  page: number
  /** One of: carrier,benefitId,companyEeId,companyName,insured,transactionId,paymentType,month,amount,date,recon,note,ticket. */
  sortBy?: string
  sortOrder?: 'asc' | 'desc'
}

export type TransactionsSummaryParams = Omit<
  ListTransactionsParams,
  'limit' | 'page' | 'sortBy' | 'sortOrder'
>

// ── money wire conversion ────────────────────────────────────────────────────
// The reconciliation-v2 transactions API speaks integer CENTS on the wire (money
// fields suffixed `*Cents`). The view-model interfaces above stay in DOLLARS so
// this page's components/helpers are unchanged; responses are mapped
// cents→dollars here — exactly ONCE — via the shared `centsToDollars` boundary
// helper (same anti-corruption pattern as reconciliation-v2-api-client.ts).

interface TransactionViewAllocationWire
  extends Omit<TransactionViewAllocation, 'amount'> {
  /** Signed integer cents (tolerates a bigint-serialized string). */
  amountCents: number | string
}

interface TransactionViewAbsorptionWire
  extends Omit<TransactionViewAbsorption, 'amount'> {
  /** Positive integer cents (tolerates a bigint-serialized string). */
  amountCents: number | string
}

interface TransactionViewRowWire
  extends Omit<
    TransactionViewRow,
    | 'amount'
    | 'allocatedAmount'
    | 'allocations'
    | 'absorptions'
    | 'absorbedAmount'
  > {
  amountCents: number
  allocatedAmountCents: number
  allocations: TransactionViewAllocationWire[]
  absorptions: TransactionViewAbsorptionWire[]
  absorbedAmountCents: number
}

interface ListTransactionsResponseWire
  extends Omit<ListTransactionsResponse, 'data'> {
  data: TransactionViewRowWire[]
}

interface TransactionSummaryResponseWire
  extends Omit<
    TransactionSummaryResponse,
    'totalAmount' | 'absorbedAmount' | 'returnedCount' | 'returnedAmount'
  > {
  totalAmountCents: number
  absorbedAmountCents: number
  returnedCount?: number
  returnedAmountCents?: number
}

// Allocation `amount` stays a signed decimal STRING (matches the prior wire
// contract downstream code reads via `Number(...)`); the row/summary totals are
// plain dollar numbers.
const mapAllocation = (
  wire: TransactionViewAllocationWire
): TransactionViewAllocation => {
  const { amountCents, ...rest } = wire
  return { ...rest, amount: centsToDollars(Number(amountCents)).toFixed(2) }
}

// `note` rides the `...rest` spread unchanged — it is a plain string on both
// sides of the wire, so only `amountCents` needs converting.
const mapAbsorption = (
  wire: TransactionViewAbsorptionWire
): TransactionViewAbsorption => {
  const { amountCents, ...rest } = wire
  return { ...rest, amount: centsToDollars(Number(amountCents)) }
}

const mapRow = (wire: TransactionViewRowWire): TransactionViewRow => {
  const {
    amountCents,
    allocatedAmountCents,
    allocations,
    absorptions,
    absorbedAmountCents,
    ...rest
  } = wire
  return {
    ...rest,
    amount: centsToDollars(amountCents),
    allocatedAmount: centsToDollars(allocatedAmountCents),
    allocations: allocations.map(mapAllocation),
    absorptions: (absorptions ?? []).map(mapAbsorption),
    absorbedAmount: centsToDollars(absorbedAmountCents ?? 0),
  }
}

const BASE = '/reconciliation-v2/transactions'

export const getTransactions = async (
  params: ListTransactionsParams
): Promise<ListTransactionsResponse> => {
  const response = await apiClient.get<ListTransactionsResponseWire>(BASE, {
    params,
  })
  return { ...response.data, data: response.data.data.map(mapRow) }
}

export const useTransactionsQuery = (
  params: ListTransactionsParams,
  options?: Omit<
    UseQueryOptions<ListTransactionsResponse, Error, ListTransactionsResponse>,
    'queryKey' | 'queryFn'
  >
) =>
  useQuery<ListTransactionsResponse, Error, ListTransactionsResponse>({
    queryKey: ['transaction-details', params],
    queryFn: () => getTransactions(params),
    placeholderData: keepPreviousData,
    ...options,
  })

export const getTransactionsSummary = async (
  params: TransactionsSummaryParams
): Promise<TransactionSummaryResponse> => {
  const response = await apiClient.get<TransactionSummaryResponseWire>(
    `${BASE}/summary`,
    { params }
  )
  const {
    totalAmountCents,
    absorbedAmountCents,
    returnedCount,
    returnedAmountCents,
    ...rest
  } = response.data
  return {
    ...rest,
    totalAmount: centsToDollars(totalAmountCents),
    absorbedAmount: centsToDollars(absorbedAmountCents ?? 0),
    returnedCount: returnedCount ?? 0,
    returnedAmount: centsToDollars(returnedAmountCents ?? 0),
  }
}

export const useTransactionsSummaryQuery = (
  params: TransactionsSummaryParams,
  options?: Omit<
    UseQueryOptions<
      TransactionSummaryResponse,
      Error,
      TransactionSummaryResponse
    >,
    'queryKey' | 'queryFn'
  >
) =>
  useQuery<TransactionSummaryResponse, Error, TransactionSummaryResponse>({
    queryKey: ['transaction-details-summary', params],
    queryFn: () => getTransactionsSummary(params),
    placeholderData: keepPreviousData,
    ...options,
  })

export interface TransactionYearsResponse {
  /** Distinct years (by transaction_date) that have ledger data, newest first. */
  years: number[]
}

export const getTransactionYears =
  async (): Promise<TransactionYearsResponse> => {
    const response = await apiClient.get<TransactionYearsResponse>(
      `${BASE}/years`
    )
    return response.data
  }

export const useTransactionYearsQuery = (
  options?: Omit<
    UseQueryOptions<TransactionYearsResponse, Error, TransactionYearsResponse>,
    'queryKey' | 'queryFn'
  >
) =>
  useQuery<TransactionYearsResponse, Error, TransactionYearsResponse>({
    queryKey: ['transaction-details-years'],
    queryFn: getTransactionYears,
    ...options,
  })

/**
 * One transaction-grain audit event (allocate / deallocate / absorption-recorded
 * / absorption-deleted). `payload` is the event's raw JSON — money fields inside
 * it are INTEGER CENTS (formatted at the display boundary).
 */
export interface TransactionEvent {
  id: string
  transactionId: string
  eventType: string
  payload: Record<string, unknown>
  createdBy: string | null
  createdAt: string
}

export const getTransactionEvents = async (
  transactionId: string
): Promise<TransactionEvent[]> => {
  const response = await apiClient.get<TransactionEvent[]>(
    `${BASE}/${transactionId}/events`
  )
  return response.data
}

/**
 * Event history for one transaction, oldest-first (the ledger row's history
 * drawer). Disabled until a transactionId is supplied, so it only fires when a
 * detail view is open.
 */
export const useTransactionEventsQuery = (
  transactionId: string | null,
  options?: Omit<
    UseQueryOptions<TransactionEvent[], Error, TransactionEvent[]>,
    'queryKey' | 'queryFn' | 'enabled'
  >
) =>
  useQuery<TransactionEvent[], Error, TransactionEvent[]>({
    queryKey: ['transaction-events', transactionId],
    queryFn: () => getTransactionEvents(transactionId ?? ''),
    enabled: transactionId != null && transactionId.length > 0,
    ...options,
  })
